import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	Card,
	Descriptions,
	Divider,
	Pagination,
	Spin,
	Typography,
	Button,
	message,
	Form,
	Upload,
	Modal,
	Input,
} from 'antd';
import { ImportOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import remakeQueryString from '../../methods/utils/remake-query-string';
import NativeNumber from '../../component/Numeral';
import BalanceItem from './user';
import { StyledFormItem, TableHeader } from '.';
import {
	getSingleMoneyBook,
	doNotPayUser,
	importUserList,
} from '../../methods/redux/reducers/moneyBook';
import baseuri from '../../methods/baseuri';

const { Text } = Typography;

const MoneyBookDetails = () => {
	const { id } = useParams<{ id: string }>();
	const [loading, setLoading] = useState(true);
	const [refresh, setRefresh] = useState('');
	const [editVisible, setEditVisible] = useState({ status: false, id: '' });

	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [form] = Form.useForm();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isLoadingImportUsers, setIsLoadingImportUsers] = useState(false);
	const [isLoadingEdit, setIsLoadingEdit] = useState(false);
	const [amount, setAmount] = useState(0);
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadResults, setDownloadResults] = useState([] as any[]);
	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	const { singleMoneyBook } = useAppSelector((store) => store.moneyBook);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const getMoneyBook = async () => {
		setLoading(true);
		const res: any = await dispatch(getSingleMoneyBook({ i: id }));
		if (res) {
			setLoading(false);
		}
	};

	useEffect(() => {
		getMoneyBook();
	}, [id]);

	useEffect(() => {
		if (singleMoneyBook?.pagination?.itemsPerPage) {
			const queryData = Object.fromEntries(searchParams.entries());

			if (Object.keys(queryData).length === 0) {
				setSearchParams({
					pageId: '1',
					perPage: String(singleMoneyBook?.pagination?.itemsPerPage),
				});
				updateQueryString({
					pageId: '1',
					perPage: 200,
				});
			}
			dispatch(getSingleMoneyBook({ i: id, ...queryData }));
		}
	}, [
		searchParams,
		id,
		dispatch,
		singleMoneyBook?.pagination?.itemsPerPage,
		refresh,
	]);

	useEffect(() => {
		if (downloadResults.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [downloadResults]);

	if (loading) {
		return <Spin size="large" />;
	}

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const removeUserPayment = async (listId: any) => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(singleMoneyBook?.pagination?.itemsPerPage),
			});
		}

		try {
			const jobData = {
				listId,
				status: 'do_not_pay',
			};

			const res = await dispatch(doNotPayUser(jobData));
			if (res) {
				dispatch(getSingleMoneyBook({ i: id, ...queryData }));
			}
		} catch (error) {
			message.error('Failed to create money book');
		} finally {
			// setIsLoadingCreateJob(false);
		}
	};

	const activateUserPayment = async (listId: any) => {
		try {
			const queryData = Object.fromEntries(searchParams.entries());
			if (Object.keys(queryData).length === 0) {
				setSearchParams({
					pageId: '1',
					perPage: String(singleMoneyBook?.pagination?.itemsPerPage),
				});
			}

			const jobData = { listId, status: 'active' };

			const res = await dispatch(doNotPayUser(jobData));
			if (res) {
				dispatch(getSingleMoneyBook({ i: id, ...queryData }));
			}
		} catch (error) {
			message.error('Failed to create money book');
		} finally {
			// setIsLoadingCreateJob(false);
		}
	};

	const editPayrollAmount = async () => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(singleMoneyBook?.pagination?.itemsPerPage),
			});
		}
		setIsLoadingEdit(true);

		try {
			const jobData = {
				listId: editVisible.id,
				amount,
			};

			const res = await dispatch(doNotPayUser(jobData));
			if (res) {
				setIsLoadingEdit(false);
				setEditVisible({ status: false, id: '' });
				dispatch(getSingleMoneyBook({ i: id, ...queryData }));
			}
		} catch (error) {
			message.error('Failed to update money book');
		} finally {
			setIsLoadingEdit(false);
		}
	};

	// Transform raw money book items into flat CSV rows
	const transformDownloadData = (items: any[]) =>
		items?.map((data: any) => ({
			email: data?.email,
			amount: data?.amount,
		    salaryInNaira: data?.salaryInNaira,
			nairaRate: data?.nairaRate,
			status: data?.status,
			createdAt: data?.createdAt
				? moment(data.createdAt).format('lll')
				: '--',
			updatedAt: data?.updatedAt
				? moment(data.updatedAt).format('lll')
				: '--',
		}));

	const csvHeaders = [
		{ label: 'Email', key: 'email' },
		{ label: 'Amount (USDT)', key: 'amount' },
		{ label: 'Amount In Local Currency (NGN)', key: 'salaryInNaira' },
		{ label: 'Exchange Rate (NGN)', key: 'nairaRate' },
		{ label: 'Status', key: 'status' },
		{ label: 'Created', key: 'createdAt' },
		{ label: 'Last Updated', key: 'updatedAt' },
	];

	const handleDownload = () => {
		setIsDownloading(true);
		const queryData = Object.fromEntries(searchParams.entries());
		setDownloadResults([]);
		baseuri
			.get('/admin/master/money_book/manage', {
				params: {
					...queryData,
					i: id,
					perPage: singleMoneyBook?.pagination?.totalItems || 1000,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawItems = res.data.data.moneyBooks;
				const transformed = transformDownloadData(rawItems);
				setDownloadResults(transformed);
				setIsDownloading(false);
			})
			.catch(() => {
				message.error('Could not download CSV');
				setIsDownloading(false);
			});
	};

	const handleSubmit = async (values: any) => {
		try {
			setIsLoadingImportUsers(true);

			const formData = new FormData();
			formData.append('bulkSenderBookId', singleMoneyBook?.profile?._id);
			formData.append('currencyId', singleMoneyBook?.profile?.currency?._id);
			formData.append('csv_file', values.userList.file);

			const jobData = {
				bulkSenderBookId: singleMoneyBook?.profile?._id,
				currencyId: singleMoneyBook?.profile?.currency?._id,
			};

			await dispatch(importUserList({ params: jobData, payload: formData }));

			handleCancel();
			form.resetFields();
		} catch (error) {
			message.error('Failed to create bulk sender job');
		} finally {
			setIsLoadingImportUsers(false);
		}
	};

	return (
		<Card title="Payroll Details" style={{ margin: '20px' }}>
			<Descriptions bordered column={2}>
				<Descriptions.Item label="Title">
					{singleMoneyBook?.profile ? (
						<Text strong>{singleMoneyBook?.profile?.title}</Text>
					) : (
						'--'
					)}
				</Descriptions.Item>
				<Descriptions.Item label="Currency">
					{singleMoneyBook?.profile ? (
						<Text strong>
							{singleMoneyBook?.profile?.currency?.name} (
							{singleMoneyBook?.profile?.currency?.symbol})
						</Text>
					) : (
						'--'
					)}
				</Descriptions.Item>
				<Descriptions.Item label="Total Users">
					<Text strong>{singleMoneyBook?.total_user}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Amount">
					<Text strong>
						<NativeNumber
							value={singleMoneyBook?.total_amount}
							decimalPlaces={2}
						/>
					</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Total Payable Users">
					<Text strong>{singleMoneyBook?.total_payable_users}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Total Payable Amount">
					<Text strong>
						<NativeNumber value={singleMoneyBook?.total_payable_amount} />
					</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Funder">
					<Text strong>{singleMoneyBook?.profile?.funder?.email}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Status">
					<Text
						style={{
							color:
								singleMoneyBook?.profile?.status === 'active' ? 'green' : 'red',
						}}
					>
						{singleMoneyBook?.profile?.status}
					</Text>
				</Descriptions.Item>
			</Descriptions>

			<br />

			<p>
				Total of{' '}
				<strong>{singleMoneyBook?.pagination?.totalItems} items</strong> found
			</p>

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={singleMoneyBook?.pagination?.totalItems}
				current={singleMoneyBook?.pagination?.currentPage}
				pageSize={singleMoneyBook?.pagination?.itemsPerPage}
				showSizeChanger
				showQuickJumper
			/>

			<br />
			<div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
				<Button type="primary" icon={<ImportOutlined />} onClick={showModal}>
					Import Users
				</Button>
				<Button
					danger
					icon={<DownloadOutlined />}
					loading={isDownloading}
					onClick={handleDownload}
				>
					{isDownloading ? 'Loading CSV...' : 'Download CSV'}
				</Button>
				<CSVLink
					data={downloadResults}
					headers={csvHeaders}
					className="download"
					filename={`Payroll - ${singleMoneyBook?.profile?.title || id}.csv`}
					ref={csvLinkRef}
				/>
			</div>

			<TableHeader
				style={{
					gridTemplateColumns: ' 40px 1fr 1fr 120px 120px 120px 100px 120px',
				}}
			>
				<div>S/N</div>
				<div>Email</div>
				<div>Name</div>
				<div>Amount (USDC)</div>
				<div>Rate (Naira)</div>
				<div>Salary (Naira)</div>
				<div>Status</div>
				<div>Action</div>
			</TableHeader>

			{singleMoneyBook?.moneyBooks?.map((item: any, index: number) => (
				<BalanceItem
					key={item._id}
					item={item}
					index={index + 1}
					removeUserPayment={removeUserPayment}
					activateUserPayment={activateUserPayment}
					setRefresh={setRefresh}
					setEditVisible={setEditVisible}
				/>
			))}

			<br />

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={singleMoneyBook?.pagination?.totalItems}
				current={singleMoneyBook?.pagination?.currentPage}
				pageSize={singleMoneyBook?.pagination?.itemsPerPage}
				showSizeChanger
				showQuickJumper
			/>

			{/* Modal for Import Users into a Book */}
			<Modal
				title={`Import Users to this Book: ${singleMoneyBook?.profile?.title}`}
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<div style={{ marginBottom: '24px' }}>
						<p style={{ marginBottom: '16px' }}>
							Import Users from CSV: users existing in book and users not
							registered in app will be omitted.
						</p>
						<div style={{ fontSize: '13px', color: '#666' }}>
							<p style={{ marginBottom: '8px' }}>
								<strong>Instructions:</strong>
							</p>
							<ul style={{ margin: 0, paddingLeft: '20px' }}>
								<li>To update: just change the amount in new sheet</li>
								<li>To remove: set amount to 0</li>
								<li>To activate deleted: set amount</li>
							</ul>
						</div>
					</div>

					<StyledFormItem
						name="userList"
						label="User List * (non-users will be omitted)"
						style={{ marginBottom: '0px' }}
						rules={[{ required: true, message: 'Please upload a CSV file!' }]}
					>
						<Upload beforeUpload={() => false} accept=".csv">
							<Button icon={<UploadOutlined />}>Choose file</Button>
						</Upload>
					</StyledFormItem>
					<p style={{ marginBottom: '24px', fontSize: '12px' }}>
						<a
							href={`${process.env.REACT_APP_BASEURI}/static/front/assets/files/sample_bulk_sender.csv`}
							style={{ color: 'purple' }}
						>
							See sample CSV file (A=email, B=amount)
						</a>
					</p>

					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoadingImportUsers}
						>
							{isLoadingImportUsers ? 'Importing...' : 'Import'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			{/* Modal for edit MoneyBook */}
			<Modal
				footer={null}
				visible={editVisible.status}
				onCancel={() => setEditVisible({ status: false, id: '' })}
				title="Edit Payroll"
				// width={500}
			>
				<Form onFinish={editPayrollAmount}>
					<StyledFormItem
						name="amount"
						label="Update Amount"
						rules={[{ required: true, message: 'Please input the amount!' }]}
					>
						<Input
							placeholder="Update Amount"
							onChange={(e: any) => setAmount(e.target.value)}
						/>
					</StyledFormItem>

					<Form.Item>
						<Button type="primary" htmlType="submit" loading={isLoadingEdit}>
							{isLoadingEdit ? 'Editing...' : 'Edit Payroll'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
};

export default MoneyBookDetails;
